import { CssBaseline, ThemeProvider } from '@mui/material';
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { createKnowMeTheme } from '@/config/theme/theme.ts';

type ThemeMode = 'dark' | 'light';

interface ThemeModeContextValue {
  mode: ThemeMode;
  toggleMode: () => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue>({
  mode: 'dark',
  toggleMode: () => {},
});

export const useThemeMode = () => useContext(ThemeModeContext);

export const KnowMeThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    const savedMode = window.localStorage.getItem('knowme-theme-mode');
    return savedMode === 'light' ? 'light' : 'dark';
  });

  const toggleMode = useCallback(() => {
    setMode(prev => {
      const nextMode = prev === 'dark' ? 'light' : 'dark';
      window.localStorage.setItem('knowme-theme-mode', nextMode);
      return nextMode;
    });
  }, []);

  const theme = useMemo(() => createKnowMeTheme(mode), [mode]);

  return (
    <ThemeModeContext.Provider value={{ mode, toggleMode }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};

export const ForceDarkThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const darkTheme = useMemo(() => createKnowMeTheme('dark'), []);
  return <ThemeProvider theme={darkTheme}>{children}</ThemeProvider>;
};
